import React from "react"
import {
    Box,
    Grid,
    Sheet,
    Stack,
    Table,
    Link,
    Typography,
} from "@mui/joy"
import {
    HighlightBox,
    LegalFooter,
    LegalHeader,
    LegalHero,
    LegalList,
    LegalMain,
    LegalPage,
    LegalSection,
    LegalText,
    LegalToc,
} from "../components/LegalLayout"

// ─── Sub-components ────────────────────────────────────────────────────────

const ProviderChip = ({ name, role }: { name: string; role: string }) => (
    <Sheet
        variant="outlined"
        sx={{ borderRadius: "8px", p: 1.5, bgcolor: "#fff" }}>
        <Typography sx={{ fontWeight: 500, fontSize: "0.875rem", color: "var(--ls-navy)", mb: 0.3 }}>
            {name}
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: "var(--ls-muted)" }}>
            {role}
        </Typography>
    </Sheet>
)

const RightItem = ({ title, desc }: { title: string; desc: string }) => (
    <Sheet
        variant="outlined"
        sx={{
            borderTop: "3px solid var(--ls-pink)",
            borderRadius: "8px",
            p: 1.5,
            bgcolor: "#fff",
        }}>
        <Typography sx={{ fontWeight: 500, fontSize: "0.875rem", color: "var(--ls-navy)", mb: 0.4 }}>
            {title}
        </Typography>
        <Typography sx={{ fontSize: "0.8rem", color: "var(--ls-muted)" }}>
            {desc}
        </Typography>
    </Sheet>
)

// ─── Page ──────────────────────────────────────────────────────────────────

const tocItems = [
    { href: "#intro", label: "Introduction" },
    { href: "#data-collected", label: "Data We Collect" },
    { href: "#how-we-use", label: "How We Use Your Data" },
    { href: "#third-parties", label: "Third-Party Services" },
    { href: "#sharing", label: "Data Sharing" },
    { href: "#audio", label: "Audio Data" },
    { href: "#retention", label: "Data Retention" },
    { href: "#rights", label: "Your Rights" },
    { href: "#cookies", label: "Cookies" },
    { href: "#children", label: "Children's Privacy" },
    { href: "#transfers", label: "International Data Transfers" },
    { href: "#security", label: "Security" },
    { href: "#changes", label: "Changes to This Policy" },
    { href: "#contact", label: "Contact" },
]

const PrivacyPolicy = () => (
    <LegalPage>
        <LegalHeader />
        <LegalHero title="Privacy Policy" />

        <LegalMain>
            <LegalToc items={tocItems} />

            <LegalSection id="intro" num="01" title="Introduction">
                <LegalText>
                    ZeeJay Labs ("we", "our", "us") operates LinguaScore and is committed to protecting your personal
                    data. This Privacy Policy explains what data we collect, how we use it, and your rights in relation
                    to it. By using LinguaScore, you agree to the practices described in this policy.
                </LegalText>
            </LegalSection>

            <LegalSection id="data-collected" num="02" title="Data We Collect">
                <Table
                    sx={{
                        my: 2,
                        fontSize: "0.875rem",
                        "& thead th": {
                            bgcolor: "var(--ls-navy)",
                            color: "#fff",
                            fontWeight: 500,
                            fontSize: "0.8rem",
                            letterSpacing: "0.04em",
                        },
                        "& thead th:first-of-type": { borderRadius: "8px 0 0 0" },
                        "& thead th:last-of-type": { borderRadius: "0 8px 0 0" },
                        "& tbody td": { color: "#3d4f63", verticalAlign: "top", py: 1.25 },
                        "& tbody tr:nth-of-type(even) td": { bgcolor: "#fff" },
                    }}
                >
                    <thead>
                        <tr>
                            <th>Category</th>
                            <th>Data collected</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><strong>Account data</strong></td>
                            <td>Name, email address, profile picture, authentication provider (Google or Facebook)</td>
                        </tr>
                        <tr>
                            <td><strong>Exam & session data</strong></td>
                            <td>Answers, written responses, speaking recordings, grading results, scores, and AI feedback</td>
                        </tr>
                        <tr>
                            <td><strong>Payment data</strong></td>
                            <td>Transaction amounts, dates, payment method type, and provider references. Full card numbers are never stored by us.</td>
                        </tr>
                        <tr>
                            <td><strong>Device & usage data</strong></td>
                            <td>IP address, browser type, device type, OS, pages visited, and session duration</td>
                        </tr>
                        <tr>
                            <td><strong>Notification tokens</strong></td>
                            <td>Firebase Cloud Messaging tokens for delivering push notifications</td>
                        </tr>
                        <tr>
                            <td><strong>Communications</strong></td>
                            <td>Messages sent to our support team</td>
                        </tr>
                    </tbody>
                </Table>
            </LegalSection>

            <LegalSection id="how-we-use" num="03" title="How We Use Your Data">
                <LegalText>We use your personal data for the following purposes:</LegalText>
                <LegalList items={[
                    "To create and manage your account",
                    "To provide AI-powered grading and feedback on your exam practice",
                    "To process payments and manage your subscription",
                    "To send you push notifications about your results and platform updates",
                    "To improve the performance and accuracy of our AI grading systems",
                    "To detect and prevent fraud, abuse, and security incidents",
                    "To comply with our legal and financial obligations",
                ]} />
            </LegalSection>

            <LegalSection id="third-parties" num="04" title="Third-Party Services">
                <LegalText>
                    We use the following third-party services to operate LinguaScore. Each has their own privacy policy
                    and data handling practices.
                </LegalText>
                <Grid container spacing={1.5} sx={{ my: 1 }}>
                    {[
                        { name: "Google Firebase", role: "Auth, storage & notifications" },
                        { name: "Groq", role: "Speech-to-text & TTS" },
                        { name: "DeepSeek", role: "AI grading & feedback" },
                        { name: "iKeepay", role: "Payment processing" },
                        { name: "MongoDB", role: "Database storage" },
                    ].map((p) => (
                        <Grid key={p.name} xs={12} sm={6} md={4}>
                            <ProviderChip {...p} />
                        </Grid>
                    ))}
                </Grid>
            </LegalSection>

            <LegalSection id="sharing" num="05" title="Data Sharing">
                <HighlightBox>
                    <Typography>We do not sell your personal data to third parties under any circumstances.</Typography>
                </HighlightBox>
                <LegalText>
                    We share your data only with the third-party service providers listed above, and only to the extent
                    necessary to provide the LinguaScore service. We may also disclose your data where required by law
                    or to protect the rights and safety of our users.
                </LegalText>
            </LegalSection>

            <LegalSection id="audio" num="06" title="Audio Data">
                <LegalText>
                    Speaking test recordings are processed exclusively by our AI transcription service for the purpose
                    of generating feedback. They are:
                </LegalText>
                <LegalList items={[
                    "Not listened to by any human reviewer at ZeeJay Labs",
                    "Not shared with any third party beyond our transcription provider (Groq)",
                    "Permanently deleted within 90 days of grading completion",
                    "Never used for training AI models or any commercial purpose",
                ]} />
            </LegalSection>

            <LegalSection id="retention" num="07" title="Data Retention">
                <LegalText>
                    We retain your data in accordance with our{" "}
                    <Link href="/data-retention" sx={{ color: "var(--ls-pink)" }}>Data Retention Policy</Link>.
                    In summary: account data is kept for as long as your account is active, exam data for 12 months
                    after your subscription ends, audio recordings for 90 days after grading, and payment records for
                    7 years to meet financial regulations.
                </LegalText>
            </LegalSection>

            <LegalSection id="rights" num="08" title="Your Rights">
                <LegalText>
                    Depending on your location, you may have the following rights regarding your personal data:
                </LegalText>
                <Grid container spacing={1.5} sx={{ my: 1 }}>
                    {[
                        { title: "Access", desc: "Request a copy of the data we hold about you" },
                        { title: "Correction", desc: "Ask us to correct inaccurate data" },
                        { title: "Deletion", desc: "Request permanent deletion of your data" },
                        { title: "Portability", desc: "Receive your data in a structured format" },
                        { title: "Withdraw consent", desc: "Opt out of non-essential data processing" },
                    ].map((r) => (
                        <Grid key={r.title} xs={12} sm={6} md={4}>
                            <RightItem {...r} />
                        </Grid>
                    ))}
                </Grid>
                <LegalText>
                    To exercise any of these rights, contact us at{" "}
                    <Link href="mailto:privacy@zeejaylabs.dev" sx={{ color: "var(--ls-pink)" }}>
                        privacy@zeejaylabs.dev
                    </Link>. We will respond within 30 days.
                </LegalText>
            </LegalSection>

            <LegalSection id="cookies" num="09" title="Cookies">
                <LegalText>
                    LinguaScore uses session cookies to maintain your login state. We do not use third-party tracking
                    or advertising cookies. You can disable cookies in your browser settings, though this may affect
                    the functionality of the platform.
                </LegalText>
            </LegalSection>

            <LegalSection id="children" num="10" title="Children's Privacy">
                <LegalText>
                    LinguaScore is not intended for children under the age of 16. We do not knowingly collect personal
                    data from children under 16. If you believe a child has provided us with their data without
                    parental consent, please contact us and we will delete it promptly.
                </LegalText>
            </LegalSection>

            <LegalSection id="transfers" num="11" title="International Data Transfers">
                <LegalText>
                    Your data may be processed and stored on servers located outside your country of residence,
                    including in the United States and Europe, where our third-party service providers operate. By
                    using LinguaScore, you consent to such transfers. We ensure that all transfers are conducted in
                    accordance with applicable data protection laws.
                </LegalText>
            </LegalSection>

            <LegalSection id="security" num="12" title="Security">
                <LegalText>
                    We implement industry-standard security measures including encryption in transit and at rest,
                    strict access controls, and regular security reviews. However, no system is completely secure and
                    we cannot guarantee absolute security. If you believe your account has been compromised, contact
                    us immediately.
                </LegalText>
            </LegalSection>

            <LegalSection id="changes" num="13" title="Changes to This Policy">
                <LegalText>
                    We may update this Privacy Policy from time to time. We will notify you of significant changes via
                    email or through the platform. Your continued use of LinguaScore after any changes constitutes
                    your acceptance of the updated policy.
                </LegalText>
            </LegalSection>

            <LegalSection id="contact" num="14" title="Contact">
                <HighlightBox>
                    <Typography>
                        If you have any questions about this Privacy Policy or how we handle your data, contact us at{" "}
                        <Link href="mailto:privacy@zeejaylabs.dev" sx={{ color: "var(--ls-pink)" }}>
                            privacy@zeejaylabs.com
                        </Link>
                        <br />
                        ZeeJay Labs · Douala, Cameroon
                    </Typography>
                </HighlightBox>
            </LegalSection>
        </LegalMain>

        <LegalFooter />
    </LegalPage>
)

export default PrivacyPolicy
